import { Alert, AlertTitle, LinearProgress, Typography } from '@mui/material';
import Snackbar from '@mui/material/Snackbar';
import type {
  GridEventListener,
  GridPaginationModel,
  GridRowModel,
  GridRowModesModel,
} from '@mui/x-data-grid';
import {
  DataGrid,
  GridRowEditStopReasons,
  GridToolbarColumnsButton,
  GridToolbarContainer,
  GridToolbarFilterButton,
  GridToolbarQuickFilter,
} from '@mui/x-data-grid';
import React, { useContext, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { v4 as uuid } from 'uuid';
import TenantContext from '../../../lib/context/TenantContext';
import { useGetDefaultLedger } from '../../../lib/query/defaultLedger';
import { ValidateHeaders } from '../../../lib/services/importService';
import { getCurrentDate } from '../../../lib/utils/dateHelper';
import { uploadFileForImport } from '../../../lib/services/blob-service';
import { sendMessage } from '../../../lib/services/queue-service';
import { ImportType } from '../../../constants/importType';
import { updateEntity } from '../../../lib/services/api-service';
import { TableName } from '../../../constants/table';
import FileUpload from '../../../components/fileUpload';
import { AddComponentType } from '../../../constants/addComponentType';
import { columns } from './gridColumns';
import { useMsal } from '@azure/msal-react';
import { usePusher } from '../../../lib/hooks/usePusher';
import CustomExportButton from '../../../components/CustomExportButton';

const customClasses = {
  root: 'table-striped table-bordered',
  columnHeaderTitle: 'fw-bold',
  columnHeader: 'header',
  cell: 'cell',
  toolbarContainer: 'table-toolbar px-5',
  overlay: '',
  pagination: 'pagination',
  tooltip: 'tooltip',
  tooltipArrow: 'arrow',
};

const DefaultLedger = () => {
  const { tenant } = useContext(TenantContext);
  const {
    data: defaultLedgers,
    refetch,
    isLoading,
  } = useGetDefaultLedger(tenant.id);

  const [showErrorAlert, setShowErrorAlert] = React.useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(10);
  const [showSuccessAlert, setShowSuccessAlert] = useState<boolean>(false);
  const [rowModesModel, setRowModesModel] = React.useState<GridRowModesModel>(
    {},
  );

  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  const { accounts } = useMsal();

  const handleImportStarted = (data: any) => {
    toast.info(data.message);
    setIsProcessing(true);
  };

  const handleImportCompleted = (data: any) => {
    toast.success(data.message);
    setIsProcessing(false);
    refetch();
  };

  const handleImportFailed = (data: any) => {
    toast.error(data.message);
    setIsProcessing(false);
    refetch();
  };

  usePusher({
    channelName: `user-${accounts[0]?.idTokenClaims?.preferred_username}`,
    eventName: 'import-started',
    callback: handleImportStarted,
  });

  usePusher({
    channelName: `user-${accounts[0]?.idTokenClaims?.preferred_username}`,
    eventName: 'import-completed',
    callback: handleImportCompleted,
  });
  usePusher({
    channelName: `user-${accounts[0]?.idTokenClaims?.preferred_username}`,
    eventName: 'import-failed',
    callback: handleImportFailed,
  });

  useEffect(() => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const showError = () => {
    setShowErrorAlert(true);
  };

  const handleClose = (reason: unknown) => {
    if (reason === 'clickaway') {
      return;
    }
    setShowErrorAlert(false);
  };
  const headers = ['CompanyName', 'VendorName', 'LedgerOCR', 'Rate', 'LedgerName'];

  const onFileUpload = (file: File, reset: () => void) => {
    ValidateHeaders(file, headers).then(res => {
      if (res) {
        setShowSuccessAlert(true);
        const currentBatchId = uuid();

        const filePath = `import/${
          tenant.id
        }/default-ledger/${getCurrentDate()}/${currentBatchId}/${file?.name}`;
        setIsProcessing(true);
        uploadFileForImport(file, filePath)
          .then(() => {
            sendMessage(
              'import-master',
              JSON.stringify({
                fileName: filePath,
                tenantId: tenant.id,
                importType: ImportType.DefaultLedger,
                batchId: currentBatchId,
                userId: accounts[0]?.idTokenClaims?.preferred_username,
              }),
            );
          })
          .then(() => reset());
      } else {
        showError();
      }
    });
  };

  const onPaginationModelChange = (model: GridPaginationModel) => {
    setPage(model.page);
    setRowsPerPage(model.pageSize);
  };

  const CustomToolbar = () => {
    return (
      <GridToolbarContainer style={{ justifyContent: 'space-between' }}>
        <div>
          <GridToolbarQuickFilter />
          <GridToolbarColumnsButton />
          <GridToolbarFilterButton />
          <CustomExportButton fileName="default-ledger" />
        </div>
      </GridToolbarContainer>
    );
  };

  const handleRowModesModelChange = (newRowModesModel: GridRowModesModel) => {
    setRowModesModel(newRowModesModel);
  };

  const processRowUpdate = async (newRow: GridRowModel) => {
    const updatedRow = { ...newRow, isNew: false };
    await updateEntity(TableName.DefaultLedger, {
      PartitionKey: newRow.PartitionKey,
      RowKey: newRow.RowKey,
      LedgerOCR: newRow.LedgerOCR,
      Rate: newRow.Rate,
      LedgerName: newRow.LedgerName,
    });

    refetch();
    toast.success('Data updated');
    return updatedRow;
  };

  /* eslint no-param-reassign: ["error", { "props": false }] */
  const handleRowEditStop: GridEventListener<'rowEditStop'> = (
    params,
    event,
  ) => {
    if (params.reason === GridRowEditStopReasons.rowFocusOut) {
      event.defaultMuiPrevented = true;
    }
  };

  return (
    <div className="mt-4">
      <Typography variant="h4" sx={{ color: 'black', fontWeight: '600' }}>
        Default Ledgers
      </Typography>

      <div className="flex py-4 items-center justify-between">
        <div className="font-semibold text-xl">Organization: {tenant.name}</div>
        <FileUpload
          onFileUpload={onFileUpload}
          headers={headers}
          addComponentType={AddComponentType.DefaultLedger}
        />
      </div>
      <div className="my-2">{isProcessing && <LinearProgress />}</div>

      <DataGrid
        rows={defaultLedgers ?? []}
        columns={columns(rowModesModel, setRowModesModel, tenant.id)}
        rowModesModel={rowModesModel}
        onRowModesModelChange={handleRowModesModelChange}
        onRowEditStop={handleRowEditStop}
        processRowUpdate={processRowUpdate}
        editMode="row"
        slots={{ toolbar: CustomToolbar }}
        autoHeight
        slotProps={{
          toolbar: {
            showQuickFilter: true,
          },
        }}
        sx={{
          boxShadow: 2,
          borderColor: 'blueGrey',
          '& .MuiDataGrid-cell:hover': {
            color: 'primary.light',
          },
          '& .gridHeader--header': {
            fontSize: 'large',
          },
          '& .MuiDataGrid-columnHeaderTitle': {
            fontWeight: '700',
            color: '#020617',
          },
        }}
        loading={isLoading}
        classes={customClasses}
        pageSizeOptions={[5, 10, 20]}
        paginationModel={{ page, pageSize: rowsPerPage }}
        onPaginationModelChange={onPaginationModelChange}
      />
      <Snackbar
        open={showErrorAlert}
        autoHideDuration={3000}
        onClose={(_event, reason) => handleClose(reason)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
        <Alert
          variant="filled"
          onClose={() => setShowErrorAlert(false)}
          severity="error"
          sx={{ width: '100%' }}>
          <AlertTitle>VALIDATION FAILED:</AlertTitle>
          FILE FORMAT DID NOT MATCH
        </Alert>
      </Snackbar>
      <Snackbar
        open={showSuccessAlert}
        autoHideDuration={1500}
        onClose={() => setShowSuccessAlert(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
        <Alert
          variant="filled"
          onClose={() => setShowErrorAlert(false)}
          severity="success"
          sx={{ width: '100%' }}>
          <AlertTitle>VALIDATION PASSED:</AlertTitle>
          UPLOADING FILE FOR PROCESSING.
        </Alert>
      </Snackbar>
    </div>
  );
};

export default DefaultLedger;
